import React, { Fragment, useRef } from "react";
import { useFormikContext } from "formik";
import PanelHeader from "../PanelHeader";
import get from "lodash/get";
import ENTITIES from "components/FormerEditor/common/constants/entity";
import { STATE_KEYS } from "utils/constants/state";
import NavigationBlock from "./NavigationBlock";
import BannerBlock from "./BannerBlock";
import SocialProfileBlock from "./SocialProfileBlock";
import ProductCollectionBlock from "./ProductCollectionBlock";
import FormBlock from "./FormBlock";
import PaymentBlock from "./PaymentBlock";
import PricingTableBlock from "./PricingTableBlock";
import FeatureComparisonBlock from "./FeatureComparisonBlock";
import FooterBlock from "./FooterBlock";
import cloneDeep from "lodash/cloneDeep";
import BlockMenuCTA from "components/BlockMenuCTA";
import OverscrollContent from "components/OverscrollContent";
import { getManifestEntity } from "utils/manifest";

const BLOCK_SETTINGS_COMPONENTS = {
  [ENTITIES.NAVIGATION]: NavigationBlock,
  [ENTITIES.BANNER]: BannerBlock,
  [ENTITIES.PRICING_TABLE]: PricingTableBlock,
  [ENTITIES.FEATURE_COMPARISON]: FeatureComparisonBlock,
  [ENTITIES.SOCIAL_PROFILE]: SocialProfileBlock,
  [ENTITIES.PRODUCT_COLLECTION]: ProductCollectionBlock,
  [ENTITIES.FORM]: FormBlock,
  [ENTITIES.PAYMENT]: PaymentBlock,
  [ENTITIES.FOOTER]: FooterBlock
};

/**
 * Per component values to reset the menu back to its root state
 */
const RESET_MENU_MAP = {
  [ENTITIES.PRICING_TABLE]: {
    key: STATE_KEYS.EDITOR.PRICING,
    value: {
      intent: null,
      activeSection: null,
      activePrice: null,
      activeProduct: null
    }
  },
  [ENTITIES.PRODUCT_COLLECTION]: {
    key: STATE_KEYS.EDITOR.MENU,
    value: {
      intent: null,
      activeIndex: null
    }
  },
  [ENTITIES.FORM]: {
    key: STATE_KEYS.EDITOR.MENU,
    value: {
      intent: null,
      activeIndex: null
    }
  }
};

const BlockSettings = () => {
  const { values } = useFormikContext();
  /**
   * TODO: create and use 'useActiveBlock' hook
   */
  const activeBlock = get(values, STATE_KEYS.EDITOR.ACTIVE_BLOCK);
  const entityMatchIndex = get(
    values,
    STATE_KEYS.MANIFEST.ENTITIES,
    []
  ).findIndex(({ uuid }) => uuid === activeBlock);

  let content = null;
  const noBlockFound = entityMatchIndex === -1;
  if (noBlockFound) {
    content = (
      <OverscrollContent>
        <BlockMenuCTA
          header="No block found"
          copy="We can't find the block settings you're looking for. Please choose another."
          cta="Close menu"
          focus={[
            {
              key: STATE_KEYS.EDITOR.VISIBILITY_PANEL,
              value: false
            }
          ]}
        />
      </OverscrollContent>
    );
  } else {
    const ctxBlock = get(
      values,
      `${STATE_KEYS.MANIFEST.ENTITIES}[${entityMatchIndex}]`
    );
    const uuid = ctxBlock.uuid;
    const name = `${STATE_KEYS.MANIFEST.CONTENT}.${uuid}`;

    const ItemComponent = BLOCK_SETTINGS_COMPONENTS[ctxBlock.component];

    const resetValues = cloneDeep(RESET_MENU_MAP[ctxBlock.component]);

    content = (
      <Fragment>
        <PanelHeader
          key={uuid}
          uuid={uuid}
          uid={ctxBlock.uid}
          title={ctxBlock.schema.title}
          resetValues={resetValues}
        />
        {ItemComponent && (
          <OverscrollContent intent={uuid}>
            <ItemComponent key={name} name={name} {...ctxBlock} />
          </OverscrollContent>
        )}
      </Fragment>
    );
  }

  return content;
};

export default BlockSettings;
